import {
	Home,
	Settings,
	Layout,
	LogOut,
	Bug,
	Star,
	Lightbulb,
	Map,
	MapPin,
	User,
	Building,
	ChevronDown,
	Plug,
	MessageSquare,
	MessageSquareText,
	CircleDollarSign,
	Users,
	Bell,
	AtSign,
	Mail,
	FileText,
} from 'lucide-react'
import {
	Sidebar,
	SidebarContent,
	SidebarGroup,
	SidebarGroupContent,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarMenuSub,
	SidebarMenuSubButton,
	SidebarMenuSubItem,
	SidebarFooter,
	SidebarTrigger,
	useSidebar,
} from '@/components/ui/sidebar'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { useAuth } from '@/context/AuthContext'
import { useToast } from '@/components/ui/use-toast'
import { Badge } from '@/components/ui/badge'
import { UpgradeButton } from '@/components/ui/UpgradeButton'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { ProgramSelector } from '@/components/ProgramSelector'
import { useTeamSubscription } from '@/hooks/useTeamSubscription'
import { Skeleton } from '@/components/ui/skeleton'
import { config } from '../../config'
import { useState, useEffect } from 'react'
import { useTeamContext } from '@/context/TeamContext'
import { useProgramContext } from '@/context/ProgramContext'
import {
	canManageProjectSettings,
	canManageWidgets,
	isAccountOwner,
} from '@/lib/programPermissions'
import { SidebarLimitIndicator } from '@/components/limits'
import { useTranslation } from '@/hooks/useTranslation'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'

// Add type definition at the top of the file after imports
type MenuItem = {
	title: string
	url: string
	icon: React.ComponentType<{ className?: string }>
	action?: {
		label: string
		onClick: (navigate: (path: string) => void) => void
	}
	isExternal?: boolean
	isSpecial?: boolean
}

type MenuItems = {
	main: MenuItem[]
	creation: MenuItem[]
	settings: MenuItem[]
}

const getMenuItems = (t: any): MenuItems => ({
	main: [
		{
			title: t('sidebar.dashboard'),
			url: '/dashboard',
			icon: Home,
		},
		{
			title: t('sidebar.widgets'),
			url: '/widgets',
			icon: Layout,
		},
	],
	creation: [
		{
			title: t('sidebar.responses'),
			url: '/responses',
			icon: MessageSquare,
		},
		{
			title: t('sidebar.inbox'),
			url: '/inbox',
			icon: MessageSquareText,
		},
		{
			title: t('sidebar.reviews'),
			url: '/reviews',
			icon: Star,
		},
		{
			title: t('sidebar.brandMentions'),
			url: '/brand-mentions',
			icon: AtSign,
		},
		{
			title: t('sidebar.bugReports'),
			url: '/bugs',
			icon: Bug,
		},
		{
			title: t('sidebar.mailbox', 'Mailbox'),
			url: '/mailbox',
			icon: Mail,
		},
		{
			title: t('sidebar.featureRequests'),
			url: '/features',
			icon: Lightbulb,
		},
		{
			title: t('sidebar.roadmap'),
			url: '/roadmap',
			icon: Map,
		},
	],
	settings: [
		{
			title: t('sidebar.accountSettings'),
			url: '/settings',
			icon: User,
		},
		{
			title: t('sidebar.notificationSettings'),
			url: '/notification-settings',
			icon: Bell,
		},
		{
			title: t('sidebar.liveChat'),
			url: '/live-chat',
			icon: MessageSquare,
		},
		{
			title: t('sidebar.members'),
			url: '/members',
			icon: Users,
		},
		{
			title: t('sidebar.integrations'),
			url: '/integrations',
			icon: Plug,
		},
		{
			title: t('sidebar.projectSettings'),
			url: '/program-settings',
			icon: Building,
		},
		{
			title: t('sidebar.roadmapSettings'),
			url: '/roadmap-settings',
			icon: MapPin,
		},
		{
			title: t('sidebar.knowledgeBase', 'AI Knowledge Base'),
			url: '/knowledge-base',
			icon: FileText,
		},
	],
})

export function AppSidebar() {
	const { t } = useTranslation()
	const location = useLocation()
	const navigate = useNavigate()
	const { toast } = useToast()
	const authContext = useAuth()
	const { teamId, isLoading: isTeamLoading, teamData, role: teamRole } = useTeamContext()
	const { currentProgram, isInvitedProgram, currentProgramAccessLevel } = useProgramContext()
	const { signOut, loading: authLoading } = authContext
	const { state, setOpenMobile } = useSidebar()
	const isCollapsed = state === 'collapsed'
	const { data: subscriptionData, isLoading: isSubscriptionLoading } =
		useTeamSubscription(teamId)
	const isPremium = subscriptionData?.isPremium || false
	const items = getMenuItems(t)
	const [isSettingsOpen, setIsSettingsOpen] = useState(() => {
		// Check if any settings route is active to determine initial state
		return items.settings.some((item) => location.pathname.startsWith(item.url))
	})
	const [isLoggingOut, setIsLoggingOut] = useState(false)
	const [showMembersNewBadge, setShowMembersNewBadge] = useState(false)
	const [showInboxNewBadge, setShowInboxNewBadge] = useState(false)

	// Check if we should show the "New" badge for Members
	useEffect(() => {
		const featureLaunchDate = localStorage.getItem('members-feature-launch')
		const currentDate = new Date()

		let shouldShowBadge = false

		if (!featureLaunchDate) {
			// First time viewing, set launch date
			localStorage.setItem('members-feature-launch', currentDate.toISOString())
			shouldShowBadge = true
		} else {
			// Check if it's been less than a week
			const launchDate = new Date(featureLaunchDate)
			const diffInDays = Math.floor((currentDate.getTime() - launchDate.getTime()) / (1000 * 60 * 60 * 24))

			if (diffInDays < 7) {
				shouldShowBadge = true
			}
		}

		setShowMembersNewBadge(shouldShowBadge)
	}, [])

	// Check if we should show the "New" badge for Inbox
	useEffect(() => {
		const inboxUpdateDate = localStorage.getItem('inbox-update-launch')
		const currentDate = new Date()

		let shouldShowBadge = false

		if (!inboxUpdateDate) {
			// First time viewing, set launch date
			localStorage.setItem('inbox-update-launch', currentDate.toISOString())
			shouldShowBadge = true
		} else {
			// Check if it's been less than 2 weeks (14 days)
			const launchDate = new Date(inboxUpdateDate)
			const diffInDays = Math.floor((currentDate.getTime() - launchDate.getTime()) / (1000 * 60 * 60 * 24))

			if (diffInDays < 14) {
				shouldShowBadge = true
			}
		}

		setShowInboxNewBadge(shouldShowBadge)
	}, [])

	// Cleanup on unmount if logging out
	useEffect(() => {
		return () => {
			if (isLoggingOut) {
				// Cleanup logic can be added here if needed
			}
		}
	}, [isLoggingOut])

	const handleLogout = async () => {
		// Prevent multiple logout attempts
		if (isLoggingOut || authLoading) {
			return
		}

		try {
			setIsLoggingOut(true)

			// Sign out and wait for completion
			await signOut()

			// Close mobile sidebar if open
			setOpenMobile(false)

			// Give a brief moment for state to propagate
			await new Promise(resolve => setTimeout(resolve, 300))

			// Force navigation to auth page and reload to ensure clean state
			window.location.href = '/auth'
		} catch (error) {
			// Reset state on error
			setIsLoggingOut(false)
			const errorMessage = error instanceof Error ? error.message : t('sidebar.failedToLogout')
			toast({
				variant: 'destructive',
				title: t('sidebar.logoutError'),
				description: errorMessage,
			})
		}
	}

	const handleItemClick = () => {
		setOpenMobile(false)
	}

	const renderMenuItem = (item: MenuItem, isLoading: boolean) => {
		const isActive =
			!item.isExternal && (
				location.pathname === item.url ||
				location.pathname.startsWith(item.url + '/')
			)

		if (isLoading) {
			return (
				<SidebarMenuItem key={item.title} className="mb-1">
					{isCollapsed ? (
						<Skeleton className="h-8 w-8 rounded-lg bg-slate-200 dark:bg-slate-700 " />
					) : (
						<div className="flex items-center gap-3 px-3 py-2 h-[36px]">
							<Skeleton className="h-5 w-5 rounded bg-slate-200 dark:bg-slate-700" />
							<Skeleton className="h-4 w-full rounded bg-slate-200 dark:bg-slate-700" />
						</div>
					)}
				</SidebarMenuItem>
			)
		}

		const linkContent = (
			<>
				<item.icon className="h-4 w-4" />
				{item.title === t('sidebar.members') && showMembersNewBadge && (
					<span className="absolute -top-1 -right-1 flex h-2 w-2">
						<span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
						<span className="relative inline-flex rounded-full h-2 w-2 bg-purple-500"></span>
					</span>
				)}
				{item.title === t('sidebar.inbox') && showInboxNewBadge && (
					<span className="absolute -top-1 -right-1 flex h-2 w-2">
						<span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
						<span className="relative inline-flex rounded-full h-2 w-2 bg-purple-500"></span>
					</span>
				)}
			</>
		)

		const expandedContent = (
			<>
				<div className="flex items-center gap-3">
					<item.icon className="h-4 w-4" />
					<span className="flex items-center gap-2">
						{item.title}

					</span>
				</div>
				{item.action && (
					<Badge
						variant="secondary"
						className={cn(
							'cursor-pointer transition-colors text-xs',
							isActive
								? 'bg-primary-foreground/20 hover:bg-primary-foreground/30 text-primary-foreground'
								: 'bg-primary/10 hover:bg-primary/20 text-primary'
						)}
						onClick={(e) => {
							e.preventDefault()
							e.stopPropagation()
							item.action.onClick(navigate)
						}}>
						{item.action.label}
					</Badge>
				)}
			</>
		)

		if (item.isExternal) {
			return (
				<SidebarMenuItem key={item.title} className={item.isSpecial ? "mt-2" : ""}>
					{isCollapsed ? (
						<Tooltip>
							<TooltipTrigger asChild>
								<SidebarMenuButton asChild>
									<a
										href={item.url}
										target="_blank"
										rel="noopener noreferrer"
										onClick={handleItemClick}
										className={cn(
											'flex items-center justify-center rounded-lg p-2 text-base transition-colors relative',
											item.isSpecial
												? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:from-green-600 hover:to-emerald-600 shadow-md hover:shadow-lg'
												: 'hover:bg-gray-100 dark:hover:bg-gray-800'
										)}>
										{linkContent}
									</a>
								</SidebarMenuButton>
							</TooltipTrigger>
							<TooltipContent side="right">
								<p>{item.title}</p>
							</TooltipContent>
						</Tooltip>
					) : (
						<SidebarMenuButton asChild>
							<a
								href={item.url}
								target="_blank"
								rel="noopener noreferrer"
								onClick={handleItemClick}
								className={cn(
									'flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm transition-all group/link',
									item.isSpecial
										? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:from-green-600 hover:to-emerald-600 shadow-md hover:shadow-lg font-medium'
										: 'hover:bg-gray-100 dark:hover:bg-gray-800'
								)}>
								{expandedContent}
							</a>
						</SidebarMenuButton>
					)}
				</SidebarMenuItem>
			)
		}

		return (
			<SidebarMenuItem key={item.title} className="">
				{isCollapsed ? (
					<Tooltip>
						<TooltipTrigger asChild>
							<SidebarMenuButton asChild>
								<Link
									to={item.url}
									onClick={handleItemClick}
									className={cn(
										'flex items-center justify-center rounded-lg p-2 text-base transition-colors relative',
										isActive
											? 'bg-primary text-primary-foreground hover:bg-primary/90'
											: 'hover:bg-gray-100 dark:hover:bg-gray-800'
									)}>
									{linkContent}
								</Link>
							</SidebarMenuButton>
						</TooltipTrigger>
						<TooltipContent side="right">
							<p>{item.title}</p>
						</TooltipContent>
					</Tooltip>
				) : (
					<SidebarMenuButton asChild>
						<Link
							to={item.url}
							onClick={handleItemClick}
							className={cn(
								'flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm transition-colors group/link',
								isActive
									? 'bg-primary text-primary-foreground hover:bg-primary/80'
									: 'hover:bg-gray-100 dark:hover:bg-gray-800'
							)}>
							{expandedContent}
						</Link>
					</SidebarMenuButton>
				)}
			</SidebarMenuItem>
		)
	}

	const renderSettingsSection = () => {
		if (isTeamLoading) {
			return (
				<>
					<div className="mt-6 mb-2 px-3">
						<Skeleton className="h-4 w-1/2 rounded bg-slate-200 dark:bg-slate-700" />
					</div>
					{items.settings
						.filter((item) => {
							// Only admin and manager can see integrations, program-settings, and roadmap-settings
							const restrictedSettings = ['/integrations', '/program-settings', '/roadmap-settings']
							if (restrictedSettings.includes(item.url)) {
								return canManageProjectSettings(currentProgramAccessLevel)
							}
							return true
						})
						.map((item) => renderMenuItem(item, true))}
				</>
			)
		}

		// Filter settings items based on role
		const visibleSettings = items.settings.filter((item) => {
			// Only admin and manager can see integrations, program-settings, and roadmap-settings
			const restrictedSettings = ['/integrations', '/program-settings', '/roadmap-settings']
			if (restrictedSettings.includes(item.url)) {
				return canManageProjectSettings(currentProgramAccessLevel)
			}
			return true
		})

		const hasActiveSettingsItem = visibleSettings.some(
			(item) =>
				location.pathname === item.url ||
				location.pathname.startsWith(item.url + '/')
		)

		if (isCollapsed) {
			// In collapsed mode, show settings items as separate icons
			return visibleSettings.map((item) => renderMenuItem(item, false))
		}

		return (
			<>
				<div className="mt-6 mb-2">
					<SidebarMenuItem>
						<SidebarMenuButton
							onClick={() => setIsSettingsOpen(!isSettingsOpen)}
							className={cn(
								'flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm transition-colors group/link',
								hasActiveSettingsItem
									? 'bg-primary text-primary-foreground hover:bg-primary/80'
									: isSettingsOpen
										? 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700'
										: 'hover:bg-gray-100 dark:hover:bg-gray-800'
							)}>
							<div className="flex items-center gap-3">
								<Settings className="h-4 w-4" />
								<span
									className={cn(
										'font-medium',
										hasActiveSettingsItem
											? 'text-primary-foreground'
											: 'text-muted-foreground'
									)}>
									{t('sidebar.settings')}
								</span>
							</div>
							<ChevronDown
								className={cn(
									'h-4 w-4 transition-transform duration-200',
									isSettingsOpen ? 'rotate-180' : ''
								)}
							/>
						</SidebarMenuButton>
						{isSettingsOpen && (
							<SidebarMenuSub>
								{visibleSettings.map((item) => {
									const isActive =
										!item.isExternal && (
											location.pathname === item.url ||
											location.pathname.startsWith(item.url + '/')
										)
									return (
										<SidebarMenuSubItem key={item.title}>
											{item.isExternal ? (
												<SidebarMenuSubButton asChild isActive={isActive}>
													<a
														href={item.url}
														target="_blank"
														rel="noopener noreferrer"
														onClick={handleItemClick}
														className={cn(
															item.isSpecial &&
															'bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:from-green-600 hover:to-emerald-600 font-medium'
														)}>
														<item.icon className="h-4 w-4" />
														<span className="flex items-center gap-2">
															{item.title}
														</span>
													</a>
												</SidebarMenuSubButton>
											) : (
												<SidebarMenuSubButton asChild isActive={isActive}>
													<Link to={item.url} onClick={handleItemClick}>
														<item.icon className="h-4 w-4" />
														<span className="flex items-center gap-2">
															{item.title}
														</span>
													</Link>
												</SidebarMenuSubButton>
											)}
										</SidebarMenuSubItem>
									)
								})}
							</SidebarMenuSub>
						)}
					</SidebarMenuItem>
				</div>
			</>
		)
	}

	return (
		<TooltipProvider delayDuration={0}>
			<Sidebar
				className={`!inset-y-auto !top-[var(--admin-impersonation-banner-height,0px)] !h-[calc(100svh-var(--admin-impersonation-banner-height,0px))] border-r ${state === 'expanded' && 'px-1'}`}
				collapsible="icon">
				<SidebarContent className="bg-white dark:bg-background">
					<SidebarGroup>
						<div
							className={cn(
								'w-full',
								isCollapsed
									? 'flex flex-col items-center gap-2 py-3'
									: 'flex items-center justify-between gap-2 py-4 px-2'
							)}>
							{isCollapsed ? (
								<img
									src="/icons/icon-192x192.png"
									alt="Feedbask Logo"
									className="h-8 w-8"
								/>
							) : (
								<img src={config.logo} alt="Feedbask Logo" className="h-14" />
							)}
							<SidebarTrigger
								className={cn(
									'z-10 hidden shrink-0 md:flex',
									isCollapsed ? 'relative' : 'absolute right-0 top-0'
								)}
							/>
						</div>
						{/* Program Selector */}
						<div className="mt-4 mb-4 pr-4  py-2 ">
							{isTeamLoading ? (
								isCollapsed ? (
									<div className="px-3 flex justify-center">
										<Skeleton className="h-8 w-8 rounded-lg bg-slate-200 dark:bg-slate-700" />
									</div>
								) : (
									<div className="px-3">
										<Skeleton className="h-[40px] w-full rounded-lg bg-slate-200 dark:bg-slate-700" />
									</div>
								)
							) : (
								<ProgramSelector isCollapsed={isCollapsed} />
							)}
						</div>
						<SidebarGroupContent>
							<SidebarMenu>
								{/* Main Section */}
								{items.main
									.filter((item) => {
										// Hide widgets menu for read-only access and member roles
										if (item.url === '/widgets') {
											return canManageWidgets(currentProgramAccessLevel)
										}
										return true
									})
									.map((item) => renderMenuItem(item, isTeamLoading))}

								{/* Limit Indicator - Show above Creation Section */}
								{!isTeamLoading && (
									<SidebarMenuItem>
										<SidebarLimitIndicator isCollapsed={isCollapsed} />
									</SidebarMenuItem>
								)}

								{/* Creation Section */}
								{!isCollapsed &&
									(isTeamLoading ? (
										<div className="mt-6 mb-2 px-3">
											<Skeleton className="h-4 w-1/2 rounded bg-slate-200 dark:bg-slate-700" />
										</div>
									) : (
										<div className="mt-6 mb-2 ">
											<h3 className="px-3 text-sm font-medium text-muted-foreground">
												{t('sidebar.feedbackAndRoadmap')}
											</h3>
										</div>
									))}
								{items.creation.map((item) =>
									renderMenuItem(item, isTeamLoading)
								)}

								{/* Settings Section */}
								{renderSettingsSection()}

								{/* Become an Affiliate */}
								{!isTeamLoading && (
									<SidebarMenuItem className="mt-2">
										{isCollapsed ? (
											<Tooltip>
												<TooltipTrigger asChild>
													<SidebarMenuButton asChild>
														<a
															href="https://www.refindie.com/register/201/feedbask"
															target="_blank"
															rel="noopener noreferrer"
															onClick={handleItemClick}
															className="flex items-center justify-center rounded-lg p-2 text-base transition-colors text-primary hover:bg-gray-100 dark:hover:bg-gray-800">
															<CircleDollarSign className="h-4 w-4" />
														</a>
													</SidebarMenuButton>
												</TooltipTrigger>
												<TooltipContent side="right">
													<p>{t('sidebar.becomeAffiliate')}</p>
												</TooltipContent>
											</Tooltip>
										) : (
											<SidebarMenuButton asChild>
												<a
													href="https://www.refindie.com/register/201/feedbask"
													target="_blank"
													rel="noopener noreferrer"
													onClick={handleItemClick}
													className="flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm transition-colors group/link text-primary font-bold hover:bg-gray-100 dark:hover:bg-gray-800">
													<div className="flex items-center gap-3">
														<CircleDollarSign className="h-4 w-4" />
														<span>{t('sidebar.becomeAffiliate')}</span>
													</div>
												</a>
											</SidebarMenuButton>
										)}
									</SidebarMenuItem>
								)}
								<SidebarMenuItem className="mt-2">
									{isCollapsed ? (
										<Tooltip>
											<TooltipTrigger asChild>
												<div className="flex justify-center px-1">
													<LanguageSwitcher compact />
												</div>
											</TooltipTrigger>
											<TooltipContent side="right">
												<p>{t('sidebar.language', 'Language')}</p>
											</TooltipContent>
										</Tooltip>
									) : (
										<div className="px-3">
											<LanguageSwitcher className="w-full" />
										</div>
									)}
								</SidebarMenuItem>
							</SidebarMenu>
						</SidebarGroupContent>
					</SidebarGroup>
				</SidebarContent>
				<SidebarFooter className="bg-white">
					<SidebarMenu>
						{isSubscriptionLoading ? (
							<SidebarMenuItem>
								{isCollapsed ? (
									<div className="flex items-center justify-center p-2">
										<Skeleton className="h-8 w-8 rounded-lg bg-card" />
									</div>
								) : (
									<SidebarMenuButton asChild>
										<Skeleton className="h-[52px] w-full rounded-lg bg-card" />
									</SidebarMenuButton>
								)}
							</SidebarMenuItem>
						) : (
							!isPremium && !isInvitedProgram && isAccountOwner(teamRole) && (
								<SidebarMenuItem>
									<SidebarMenuButton asChild>
										<UpgradeButton onClick={handleItemClick} />
									</SidebarMenuButton>
								</SidebarMenuItem>
							)
						)}
						<SidebarMenuItem className="bg-white">
							{isTeamLoading ? (
								isCollapsed ? (
									<div className="flex items-center justify-center p-2">
										<Skeleton className="h-8 w-8 rounded-full bg-card" />
									</div>
								) : (
									<div className="flex items-center gap-3 px-3 py-2 h-[56px]">
										<Skeleton className="h-8 w-8 rounded-full bg-card" />
										<div className="flex flex-col gap-1 flex-1">
											<Skeleton className="h-3 w-20 rounded bg-card" />
											<Skeleton className="h-3 w-16 rounded bg-card" />
										</div>
									</div>
								)
							) : (
								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<SidebarMenuButton
											className={cn(
												'flex items-center gap-3 rounded-lg transition-colors h-auto ',
												isCollapsed
													? 'justify-center p-2'
													: 'px-3 py-2 justify-start'
											)}>
											{isCollapsed ? (
												<Avatar className="h-8 w-8">
													<AvatarImage
														src={authContext.user?.user_metadata?.avatar_url}
													/>
													<AvatarFallback className="text-xs">
														{authContext.user?.user_metadata?.full_name
															?.split(' ')
															.map((n: string) => n[0])
															.join('')
															.toUpperCase() ||
															authContext.user?.email
																?.substring(0, 2)
																.toUpperCase() ||
															'U'}
													</AvatarFallback>
												</Avatar>
											) : (
												<>
													<Avatar className="h-8 w-8">
														<AvatarImage
															src={authContext.user?.user_metadata?.avatar_url}
														/>
														<AvatarFallback className="text-xs">
															{authContext.user?.user_metadata?.full_name
																?.split(' ')
																.map((n: string) => n[0])
																.join('')
																.toUpperCase() ||
																authContext.user?.email
																	?.substring(0, 2)
																	.toUpperCase() ||
																'U'}
														</AvatarFallback>
													</Avatar>
													<div className="flex flex-col flex-1 min-w-0">
														<p className="text-sm font-medium truncate">
															{authContext.user?.user_metadata?.full_name ||
																authContext.user?.email?.split('@')[0] ||
																t('sidebar.user')}
														</p>
														<p className="text-xs text-muted-foreground truncate">
															{authContext.user?.email || ''}
														</p>
													</div>
												</>
											)}
										</SidebarMenuButton>
									</DropdownMenuTrigger>
									<DropdownMenuContent
										align={isCollapsed ? 'end' : 'center'}
										side={isCollapsed ? 'right' : 'top'}
										className="w-56">
										<DropdownMenuLabel className="font-normal">
											<div className="flex flex-col space-y-1">
												<p className="text-sm font-medium leading-none">
													{authContext.user?.user_metadata?.full_name ||
														authContext.user?.email?.split('@')[0] ||
														t('sidebar.user')}
												</p>
												<p className="text-xs leading-none text-muted-foreground">
													{authContext.user?.email}
												</p>
											</div>
										</DropdownMenuLabel>
										<DropdownMenuSeparator />
										<DropdownMenuItem
											onClick={handleLogout}
											disabled={isLoggingOut}
											className={cn(
												'cursor-pointer',
												isLoggingOut && 'opacity-50 cursor-not-allowed'
											)}>
											<LogOut className="mr-2 h-4 w-4" />
											<span>{isLoggingOut ? t('sidebar.loggingOut') : t('sidebar.logout')}</span>
										</DropdownMenuItem>
									</DropdownMenuContent>
								</DropdownMenu>
							)}
						</SidebarMenuItem>
					</SidebarMenu>
				</SidebarFooter>
			</Sidebar>
		</TooltipProvider>
	)
}
